// Ported from horizontal-actions-dashboard/frontend/src/components/KpiCard.tsx,
// translated from CSS custom properties to MUI sx-based styling.

import type { ReactNode } from 'react';
import { Paper, Typography, Box } from '@mui/material';

export type KpiTone =
  | 'default'
  | 'danger'
  | 'warning'
  | 'success'
  | 'risk-critical'
  | 'risk-high'
  | 'risk-medium'
  | 'risk-low';

const TONE_STYLES: Record<KpiTone, { bg: string; fg: string; border: string }> = {
  default: { bg: '#f5f7fa', fg: '#1a2b3c', border: '#e1e5e9' },
  danger: { bg: '#fdecea', fg: '#c0392b', border: '#f5c6cb' },
  warning: { bg: '#fff8e1', fg: '#8a6d00', border: '#ffe08a' },
  success: { bg: '#e8f5e9', fg: '#1e7e34', border: '#c3e6cb' },
  'risk-critical': { bg: '#fdecea', fg: '#b71c1c', border: '#f5c6cb' },
  'risk-high': { bg: '#fff3e0', fg: '#e65100', border: '#ffccbc' },
  'risk-medium': { bg: '#fffde7', fg: '#8a6d00', border: '#fff59d' },
  'risk-low': { bg: '#e8f5e9', fg: '#1e7e34', border: '#c3e6cb' },
};

export function TerminalsKpiCard({
  label,
  value,
  hint,
  tone = 'default',
  centered = false,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: KpiTone;
  // Centre the content in the card, both ways. Used for a card that is taller than its text.
  centered?: boolean;
}) {
  const style = TONE_STYLES[tone] ?? TONE_STYLES.default;
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        backgroundColor: style.bg,
        borderColor: style.border,
        height: '100%',
        ...(centered && {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }),
      }}
    >
      <Typography variant="caption" sx={{ color: '#5a6b7c', fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="h5" sx={{ color: style.fg, fontWeight: 700, lineHeight: 1.2 }}>
          {value}
        </Typography>
      </Box>
      {hint && (
        <Typography variant="body2" sx={{ color: '#7a8a99', mt: 0.5 }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
}
